import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Tabs,
  Button,
  SurfaceCard,
  PageContainer,
} from '@react-apps/design-system';
import type { TabItem } from '@react-apps/design-system';
import type {
  TransactionDetail,
  TransactionStatus,
  BillingAgreement,
  DebtArrangementWithRelations,
} from '../../domain/entities/sales.entity';
import { useTransaction } from '../hooks/use-transaction';
import { EntityEventsSection } from '../components/EntityEventsSection';
import {
  CancelBillingAgreementModal,
  DebtArrangementReasonModal,
  AbandonContractModal,
} from '../components/sales-action-modals';
import { RenegotiationModal } from '../components/RenegotiationModal';
import { InvalidateManualTransactionModal } from '../components/sales-manual-operation-modals';
import { OfferCard } from '../components/OfferCard';
import { useRouteTabs } from '@/shared/hooks/use-route-tabs';
import { EntityLink } from '@/shared/components/entity-link';
import { formatDocumentForDisplay } from '@/shared/utils/br-fields';
import {
  useTransactionInvoices,
  useTransactionBillingAgreement,
  useTransactionOfferAndArrangements,
  useTransactionContract,
} from '../hooks/use-transaction-details';
import {
  TransactionStatusBadge,
  PaymentMethodBadge,
  BillingAgreementStatusBadge,
  DebtArrangementStatusBadge,
  ContractStatusBadge,
  Field,
  Mono,
  ProviderMetadataBlock,
  SectionCard,
  SkeletonCard,
  ErrorState,
  billingAgreementModeLabel,
  chargeIntervalLabel,
  formatCurrency,
  formatDate,
  formatDateTime,
  transactionOriginLabel,
} from '../shared/display';
import { InvoiceChargeDateCell } from '../shared/invoices';

// Não há campos dedicados de "aprovada em"/"cancelada em" no domínio da
// transação — derivamos a partir do status usando os timestamps reais que já
// temos (occurredAt = quando o gateway processou; updatedAt = última mudança
// de status), só quando o status é compatível com aquele significado.
const APPROVED_TRANSACTION_STATUSES = new Set<TransactionStatus>([
  'paid',
  'refunded',
  'chargeback',
  'disputed',
  'in_recovery',
]);

const CANCELLED_TRANSACTION_STATUSES = new Set<TransactionStatus>([
  'cancelled',
  'rejected',
  'failed',
  'chargeback',
  'refunded',
  'blocked',
  'expired',
]);

/** Ids das abas — também são os segmentos da URL (`/sales/:id/:tab`). */
const TRANSACTION_TAB_IDS = [
  'details',
  'contract',
  'billing-agreement',
  'offer',
  'audit',
];

export function TransactionDetailPage() {
  const { transactionId } = useParams<{ transactionId: string }>();
  const goBack = useBackNavigate('/sales');
  const id = transactionId ?? '';
  const { data: transaction, isLoading, isError } = useTransaction(id);
  const { activeTab, onTabChange } = useRouteTabs(
    `/sales/${id}`,
    TRANSACTION_TAB_IDS,
  );

  if (isError) {
    return (
      <PageContainer maxWidth="4xl" spacing="none">
        <SurfaceCard padding="xl" className="text-center">
          <p className="text-(--color-text-primary) font-medium">
            Transação não encontrada
          </p>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            A transação solicitada não existe ou você não tem permissão para
            visualizá-la.
          </p>
          <button
            onClick={goBack}
            className="text-(--color-accent) mt-4 text-sm hover:underline"
          >
            Voltar para Vendas
          </button>
        </SurfaceCard>
      </PageContainer>
    );
  }

  const tabs: TabItem[] = [
    {
      id: 'details',
      label: 'Detalhes',
      content: (
        <InvoicesTab
          transactionId={id}
          transaction={transaction}
          loading={isLoading}
        />
      ),
    },
    {
      id: 'contract',
      label: 'Contrato',
      content: <ContractTab transactionId={id} />,
    },
    {
      id: 'billing-agreement',
      label: 'Billing Agreement',
      content: <BillingAgreementTab transactionId={id} />,
    },
    {
      id: 'offer',
      label: 'Offer & Renegociações',
      content: <OfferArrangementsTab transactionId={id} />,
    },
    {
      id: 'audit',
      label: 'Auditoria',
      content: <EntityEventsSection entityType="Transaction" entityId={id} />,
    },
  ];

  return (
    <PageContainer maxWidth="4xl">
      {/* Voltar */}
      <button
        onClick={goBack}
        className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5 8.25 12l7.5-7.5"
          />
        </svg>
        Vendas
      </button>

      {/* Abas — cada uma busca seus dados ao abrir. A aba ativa vem da URL
          (`/sales/:id/:tab`), então o link é compartilhável. */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
    </PageContainer>
  );
}

// --- Aba: Transação ---

function TransactionTab({
  transaction,
  loading,
}: {
  transaction: TransactionDetail | undefined;
  loading: boolean;
}) {
  const [invalidating, setInvalidating] = useState(false);

  if (loading) return <SkeletonCard rows={6} />;
  if (!transaction) return <ErrorState message="Transação indisponível." />;

  const metadataEntries = transaction.providerMetadata
    ? Object.entries(transaction.providerMetadata)
    : [];

  const approved = APPROVED_TRANSACTION_STATUSES.has(transaction.status);
  const cancelled = CANCELLED_TRANSACTION_STATUSES.has(transaction.status);

  return (
    <div className="space-y-6">
      <SectionCard
        title="Detalhe da Transação"
        action={
          <div className="flex items-center gap-3">
            <TransactionStatusBadge status={transaction.status} />
            {/* Só lançamento manual é mutável por admin: transação de gateway é
                espelho de um fato que vive no provedor e se desfaz lá (o estorno
                real volta por webhook). A entidade recusa o resto no backend. */}
            {transaction.origin === 'manual' &&
              transaction.status !== 'voided' && (
                <Button
                  variant="secondary"
                  onClick={() => setInvalidating(true)}
                >
                  Anular lançamento
                </Button>
              )}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="ID externo (gateway)"
            value={<Mono>{transaction.externalTransactionId}</Mono>}
          />
          <Field label="ID" value={<Mono>{transaction.id}</Mono>} />
          <Field
            label="Fatura ID"
            value={<Mono>{transaction.invoiceId}</Mono>}
          />
          <Field
            label="Método de pagamento"
            value={
              transaction.paymentMethod ? (
                <PaymentMethodBadge method={transaction.paymentMethod} />
              ) : null
            }
          />
          <Field
            label="Origem"
            value={transactionOriginLabel(transaction.origin)}
          />
          <Field
            label="Criada em"
            value={formatDateTime(transaction.createdAt)}
          />
          <Field
            label="Atualizada em"
            value={formatDateTime(transaction.updatedAt)}
          />
          <Field
            label="Aprovada em"
            value={approved ? formatDateTime(transaction.occurredAt) : null}
          />
          <Field
            label="Liquidada em"
            value={
              transaction.settledAt
                ? formatDateTime(transaction.settledAt)
                : null
            }
          />
          <Field
            label="Cancelada em"
            value={cancelled ? formatDateTime(transaction.updatedAt) : null}
          />
        </div>
      </SectionCard>

      {metadataEntries.length > 0 && (
        <SectionCard title="Metadata do provedor">
          <ProviderMetadataBlock entries={metadataEntries} heading={null} />
        </SectionCard>
      )}

      {invalidating && (
        <InvalidateManualTransactionModal
          open
          onClose={() => setInvalidating(false)}
          transaction={transaction}
          invalidateKey={['sales', 'transaction', transaction.id]}
        />
      )}
    </div>
  );
}

// --- Aba: Invoices ---

function InvoicesTab({
  transactionId,
  transaction,
  loading,
}: {
  transactionId: string;
  transaction: TransactionDetail | undefined;
  loading: boolean;
}) {
  const { data, isLoading, isError } = useTransactionInvoices(transactionId);

  return (
    <div className="space-y-6">
      {isLoading ? (
        <SkeletonCard rows={5} />
      ) : isError || !data ? (
        <ErrorState message="Não foi possível carregar as invoices." />
      ) : data.invoices.length === 0 ? (
        <ErrorState message="Nenhuma invoice vinculada." />
      ) : (
        (() => {
          const invoice =
            data.invoices.find((i) => i.id === data.primaryInvoiceId) ??
            data.invoices[0];
          return (
            <SectionCard title="Resumo da Fatura">
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <Field
                  label="ID externo (gateway)"
                  value={
                    invoice.externalId ? (
                      <Mono>{invoice.externalId}</Mono>
                    ) : null
                  }
                />
                <Field label="ID" value={<Mono>{invoice.id}</Mono>} />
                <Field
                  label="Contrato ID"
                  value={
                    <Mono>
                      {/* `sourceId` só é um contrato quando a fonte é
                          `contract` — em `debt_arrangement` aponta pra uma
                          renegociação, que não tem tela própria. */}
                      {invoice.sourceType === 'contract' ? (
                        <EntityLink kind="contract" id={invoice.sourceId} />
                      ) : (
                        invoice.sourceId
                      )}
                    </Mono>
                  }
                />
                <Field label="Tipo de fonte" value={invoice.sourceType} />
                <Field
                  label="Parcelamento"
                  value={transaction ? `${transaction.installments}x` : null}
                />
                <Field label="Valor" value={formatCurrency(invoice.value)} />
                <Field label="Ciclo" value={invoice.cycleIndex} />
                <Field
                  label="Cobrança"
                  value={
                    invoice.chargeDate ? (
                      <InvoiceChargeDateCell invoice={invoice} />
                    ) : null
                  }
                />
                <Field
                  label="Iniciada Em"
                  value={formatDateTime(invoice.createdAt)}
                />
                <Field
                  label="Atualizada em"
                  value={formatDateTime(invoice.updatedAt)}
                />
              </div>
            </SectionCard>
          );
        })()
      )}

      <TransactionTab transaction={transaction} loading={loading} />
    </div>
  );
}

// --- Aba: Contrato + usuário ---

// Estados "vivos" em que o abandono manual faz sentido (os terminais já são
// definitivos). O backend permite mais, mas a UI só oferece nos abertos.
// `not_converted` fica fora de propósito: placeholder terminal, nada a abandonar.
const CONTRACT_ABANDONABLE_STATUSES = new Set([
  'pending_confirmation',
  'active',
  'overdue',
]);

function ContractTab({ transactionId }: { transactionId: string }) {
  const { data, isLoading, isError } = useTransactionContract(transactionId);
  const [abandonOpen, setAbandonOpen] = useState(false);

  if (isLoading) return <SkeletonCard rows={5} />;
  if (isError || !data)
    return <ErrorState message="Não foi possível carregar o contrato." />;

  const { contract, user } = data;
  const canAbandon = CONTRACT_ABANDONABLE_STATUSES.has(contract.status);

  return (
    <div className="space-y-6">
      <SectionCard
        title="Contrato"
        action={
          <div className="flex items-center gap-2">
            {canAbandon && (
              <Button variant="danger" onClick={() => setAbandonOpen(true)}>
                Abandonar contrato
              </Button>
            )}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="ID"
            value={
              <Mono>
                <EntityLink kind="contract" id={contract.id} />
              </Mono>
            }
          />
          <Field
            label="Status"
            value={<ContractStatusBadge status={contract.status} />}
          />
          <Field label="Início" value={formatDate(contract.startDate)} />
          <Field label="Fim" value={formatDate(contract.endDate)} />
          <Field
            label="BA atual"
            value={
              contract.currentBillingAgreementId ? (
                <Mono>
                  <EntityLink
                    kind="billingAgreement"
                    id={contract.currentBillingAgreementId}
                  />
                </Mono>
              ) : null
            }
          />
          <Field
            label="BA inicial"
            value={
              contract.firstBillingAgreementId ? (
                <Mono>
                  <EntityLink
                    kind="billingAgreement"
                    id={contract.firstBillingAgreementId}
                  />
                </Mono>
              ) : null
            }
          />
          <Field
            label="Mesclado no contrato"
            value={
              contract.mergedIntoContractId ? (
                <Mono>
                  <EntityLink
                    kind="contract"
                    id={contract.mergedIntoContractId}
                  />
                </Mono>
              ) : null
            }
          />
          <Field
            label="Atualizado em"
            value={formatDateTime(contract.updatedAt)}
          />
        </div>
      </SectionCard>

      <SectionCard title="Usuário do contrato">
        {!user ? (
          <p className="text-(--color-text-secondary) text-sm italic">
            Usuário não encontrado.
          </p>
        ) : (
          <div className="flex items-start gap-4">
            <div className="bg-(--color-accent)/10 text-(--color-accent) flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nome" value={user.name} />
              <Field label="E-mail" value={user.email} />
              <Field
                label="Documento"
                value={formatDocumentForDisplay(user.doc)}
              />
              <Field
                label="User ID"
                value={
                  <Mono>
                    <EntityLink kind="user" id={user.userId} />
                  </Mono>
                }
              />
              <Field label="Account ID" value={<Mono>{user.accountId}</Mono>} />
            </div>
          </div>
        )}
      </SectionCard>

      <AbandonContractModal
        invalidateKey={['sales', 'transaction', transactionId]}
        contractId={contract.id}
        open={abandonOpen}
        onClose={() => setAbandonOpen(false)}
      />
    </div>
  );
}

// --- Aba: Billing Agreement ---

// `not_converted` fica fora de propósito: placeholder terminal, nada a cancelar
// no gateway.
const BA_CANCELABLE_STATUSES = new Set([
  'waiting_payment',
  'processing',
  'trial',
  'inactive',
  'active',
  'pastdue',
]);

const DA_CHAINED_ACTIVE = ['waiting_payment', 'active', 'completed', 'broken'];

function BillingAgreementTab({ transactionId }: { transactionId: string }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [renegOpen, setRenegOpen] = useState(false);
  const { data, isLoading, isError } =
    useTransactionBillingAgreement(transactionId);
  const { data: chain, isLoading: chainLoading } =
    useTransactionOfferAndArrangements(transactionId);
  const { data: invoicesData, isLoading: invoicesLoading } =
    useTransactionInvoices(transactionId, renegOpen);
  const invalidateKey = ['sales', 'transaction', transactionId];

  if (isLoading) return <SkeletonCard rows={6} />;
  if (isError || !data)
    return (
      <ErrorState message="Não foi possível carregar a Billing Agreement." />
    );

  const interval = chargeIntervalLabel(
    data.chargeInterval,
    data.chargeIntervalUnit,
  );

  const canCancel = BA_CANCELABLE_STATUSES.has(data.status);

  const dasForBa =
    chain?.debtArrangements.filter(
      (d) => d.replacedBillingAgreement?.id === data.id,
    ) ?? [];
  const hasDraft = dasForBa.some((d) => d.status === 'draft');
  const alreadyConcluded = dasForBa.some((d) =>
    DA_CHAINED_ACTIVE.includes(d.status),
  );
  const showReneg = data.status === 'cancelled' && !alreadyConcluded;

  return (
    <div className="space-y-6">
      <SectionCard
        title="Billing Agreement"
        action={
          <div className="flex items-center gap-2">
            {canCancel ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                Cancelar BA
              </Button>
            ) : showReneg ? (
              <Button variant="secondary" onClick={() => setRenegOpen(true)}>
                {hasDraft ? 'Continuar renegociação' : 'Criar renegociação'}
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="ID"
            value={
              <Mono>
                <EntityLink kind="billingAgreement" id={data.id} />
              </Mono>
            }
          />
          <Field
            label="Status"
            value={<BillingAgreementStatusBadge status={data.status} />}
          />
          <Field label="Modo" value={billingAgreementModeLabel(data.mode)} />
          <Field label="Origem" value={data.origin} />
          <Field label="Valor total" value={formatCurrency(data.totalPrice)} />
          <Field
            label="Ciclos"
            value={data.cycleCount !== null ? String(data.cycleCount) : null}
          />
          <Field label="Intervalo de cobrança" value={interval} />
          <Field
            label="Pode cancelar via API"
            value={data.canCancel ? 'Sim' : 'Não'}
          />
          <Field label="ID externo" value={<Mono>{data.externalId}</Mono>} />
          <Field
            label="Substitui a BA"
            value={
              data.replacesBillingAgreementId ? (
                <Mono>
                  <EntityLink
                    kind="billingAgreement"
                    id={data.replacesBillingAgreementId}
                  />
                </Mono>
              ) : null
            }
          />
          <Field
            label="Último status em"
            value={formatDateTime(data.lastStatusAt)}
          />
          <Field label="Atualizada em" value={formatDateTime(data.updatedAt)} />
        </div>
      </SectionCard>

      {(data.cancelledAt ||
        data.cancelReason ||
        data.cancelledByName ||
        data.cancelledByEmail) && (
        <SectionCard title="Cancelamento">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Cancelada em"
              value={formatDateTime(data.cancelledAt)}
            />
            <Field label="Motivo" value={data.cancelReason} />
            <Field label="Por (nome)" value={data.cancelledByName} />
            <Field label="Por (e-mail)" value={data.cancelledByEmail} />
          </div>
        </SectionCard>
      )}

      {showReneg && (
        <RenegotiationModal
          invalidateKey={invalidateKey}
          billingAgreement={data}
          chain={chain}
          chainLoading={chainLoading}
          invoicesData={invoicesData}
          invoicesLoading={invoicesLoading}
          open={renegOpen}
          onClose={() => setRenegOpen(false)}
        />
      )}

      <CancelBillingAgreementModal
        title="Cancelar Billing Agreement"
        invalidateKey={invalidateKey}
        billingAgreement={data}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      />
    </div>
  );
}

// --- Aba: Offer & Renegociações ---

function OfferArrangementsTab({ transactionId }: { transactionId: string }) {
  const { data, isLoading, isError } =
    useTransactionOfferAndArrangements(transactionId);
  const [revertTarget, setRevertTarget] =
    useState<DebtArrangementWithRelations | null>(null);

  if (isLoading) return <SkeletonCard rows={4} />;
  if (isError || !data)
    return <ErrorState message="Não foi possível carregar a oferta." />;

  const currentBaId = data.billingAgreementId;

  return (
    <div className="space-y-6">
      <OfferCard offer={data.offer} />

      <SectionCard
        title={`Renegociações (Debt Arrangements) — ${data.debtArrangements.length}`}
      >
        {data.debtArrangements.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm italic">
            Nenhuma renegociação relacionada a este contrato.
          </p>
        ) : (
          <div className="space-y-5">
            {data.debtArrangements.length > 1 && (
              <p className="text-(--color-text-secondary) text-xs">
                Cadeia de {data.debtArrangements.length} renegociações, em ordem
                cronológica.
              </p>
            )}
            {data.debtArrangements.map((da, index) => (
              <div
                key={da.id}
                className="border-(--color-border) bg-(--color-bg-secondary) space-y-4 rounded-lg border p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {data.debtArrangements.length > 1 && (
                    <span className="text-(--color-text-secondary) text-xs font-semibold">
                      #{index + 1}
                    </span>
                  )}
                  <DebtArrangementStatusBadge status={da.status} />
                  <span className="text-(--color-text-secondary) text-xs">
                    {da.type}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {da.status === 'draft' && (
                      <span className="text-(--color-text-secondary) text-xs italic">
                        rascunho — edite na aba Billing Agreement
                      </span>
                    )}
                    {(da.status === 'waiting_payment' ||
                      da.status === 'active') && (
                      <Button
                        variant="danger"
                        onClick={() => setRevertTarget(da)}
                      >
                        Reverter
                      </Button>
                    )}
                  </div>
                </div>

                {/* Sequência: BA substituída → DA → BA resultante */}
                <div className="space-y-2">
                  <BaChainStep
                    role="substituída"
                    ba={da.replacedBillingAgreement}
                    currentId={currentBaId}
                  />
                  <div className="text-(--color-text-secondary) flex items-center gap-2 pl-1 text-xs">
                    <span className="text-(--color-accent)">↓</span>
                    renegociada por este Debt Arrangement
                  </div>
                  <BaChainStep
                    role="resultante"
                    ba={da.resultingBillingAgreement}
                    currentId={currentBaId}
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                  <Field
                    label="DA ID"
                    value={
                      <Mono>
                        <EntityLink kind="debtArrangement" id={da.id} />
                      </Mono>
                    }
                  />
                  <Field label="Motivo" value={da.reason} />
                  <Field
                    label="Offer externa"
                    value={
                      da.externalOfferId ? (
                        <Mono>{da.externalOfferId}</Mono>
                      ) : null
                    }
                  />
                  <Field label="Criada em" value={formatDate(da.createdAt)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {revertTarget && (
        <DebtArrangementReasonModal
          invalidateKey={['sales', 'transaction', transactionId]}
          debtArrangementId={revertTarget.id}
          action="revert"
          open={!!revertTarget}
          onClose={() => setRevertTarget(null)}
        />
      )}
    </div>
  );
}

/** Um passo da cadeia: card compacto de uma BA (substituída ou resultante). */
function BaChainStep({
  role,
  ba,
  currentId,
}: {
  role: 'substituída' | 'resultante';
  ba: BillingAgreement | null;
  currentId: string | null;
}) {
  if (!ba) {
    return (
      <div className="border-(--color-border) text-(--color-text-secondary) rounded-md border border-dashed p-3 text-xs italic">
        BA {role}: ainda não criada
      </div>
    );
  }

  const isCurrent = ba.id === currentId;

  return (
    <div
      className={[
        'rounded-md border p-3',
        isCurrent
          ? 'border-(--color-accent) bg-(--color-accent)/5'
          : 'border-(--color-border) bg-(--color-bg-primary)',
      ].join(' ')}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-(--color-text-secondary) text-[10px] font-semibold uppercase tracking-wide">
          BA {role}
        </span>
        <BillingAgreementStatusBadge status={ba.status} />
        {isCurrent && (
          <span className="text-(--color-accent) text-xs font-medium">
            • desta transação
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
        <Field label="ID externo" value={<Mono>{ba.externalId}</Mono>} />
        <Field label="Modo" value={billingAgreementModeLabel(ba.mode)} />
        <Field label="Valor total" value={formatCurrency(ba.totalPrice)} />
        <Field
          label="ID"
          value={
            <Mono>
              <EntityLink kind="billingAgreement" id={ba.id} />
            </Mono>
          }
        />
        {ba.cancelReason ? (
          <Field label="Motivo cancelamento" value={ba.cancelReason} />
        ) : null}
      </div>
    </div>
  );
}
